import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Renders a PDF inline (no browser PDF plugin needed) and provides Print +
 * Download + optional Email send button. Accepts a stable `url` (blob URL or
 * remote URL) and a friendly `filename`. Closes on ESC, click outside, or X.
 */
export default function PdfPreviewModal({ url, filename, title, onClose, onSendEmail, sending }) {
  const containerRef = useRef(null);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const renderTaskRef = useRef([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        setLoading(true);
        setError(null);
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        try {
          pdfjs.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs";
        } catch (_) { /* ignore */ }
        const buf = await (await fetch(url)).arrayBuffer();
        if (cancelled) return;
        const pdf = await pdfjs.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPages(pdf.numPages);

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const targetCssWidth = Math.max(
          400,
          Math.min(1200, (container.clientWidth || 800) - 32),
        );

        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const baseVp = page.getViewport({ scale: 1 });
          const scale = (targetCssWidth / baseVp.width);
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          canvas.style.width = `${Math.round(viewport.width / dpr)}px`;
          canvas.style.height = `${Math.round(viewport.height / dpr)}px`;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 16px";
          canvas.style.background = "white";
          canvas.style.boxShadow = "0 1px 3px rgba(0,0,0,0.25)";
          canvas.setAttribute("data-testid", `pdf-page-${n}`);
          container.appendChild(canvas);
          const ctx = canvas.getContext("2d");
          const task = page.render({ canvasContext: ctx, viewport });
          renderTaskRef.current.push(task);
          await task.promise;
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Errore caricamento PDF");
          setLoading(false);
        }
      }
    };
    render();
    return () => {
      cancelled = true;
      try { renderTaskRef.current.forEach((t) => { try { t.cancel(); } catch (_) { /* ignore */ } }); } catch (_) { /* ignore */ }
      renderTaskRef.current = [];
    };
  }, [url]);

  const handlePrint = () => {
    // Use the native PDF viewer (vector, sharp text, correct landscape detection)
    // instead of canvas snapshots (raster, blurry, ignores PDF orientation).
    if (!url) { toast.error("PDF non disponibile."); return; }
    const w = window.open(url, "_blank");
    if (!w) {
      toast.error("Il browser ha bloccato la finestra di stampa. Consenti i popup per questo sito.");
      return;
    }
    // Trigger the print dialog as soon as the PDF viewer finishes loading.
    // Most browsers (Chrome, Edge, Firefox, Safari) fire `load` on the
    // embedded PDF viewer; if it never fires (rare), the user can still print
    // manually with Ctrl+P from the opened tab.
    const tryPrint = () => {
      try { w.focus(); w.print(); } catch (_) { /* ignore cross-origin */ }
    };
    let printed = false;
    w.addEventListener("load", () => {
      if (printed) return;
      printed = true;
      setTimeout(tryPrint, 400);
    });
    // Safety net: if `load` never fires within 2s, attempt anyway.
    setTimeout(() => {
      if (printed) return;
      printed = true;
      tryPrint();
    }, 2000);
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "documento.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div
        className="bg-[var(--c-bg)] border border-[var(--c-text)] w-full max-w-6xl h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--c-text)] gap-3 flex-wrap">
          <div className="font-display text-xl truncate flex-1 min-w-0">{title || "Anteprima PDF"}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] whitespace-nowrap">
            {loading ? "Caricamento…" : error ? "Errore" : `${pages} pag.`}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {onSendEmail && (
              <button
                onClick={onSendEmail}
                disabled={loading || !!error || !!sending}
                className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white flex items-center gap-2 disabled:opacity-40 transition-colors"
                data-testid="pdf-modal-email"
              >
                {sending ? "Invio…" : "Invia Email"}
              </button>
            )}
            <button
              onClick={handlePrint}
              disabled={loading || !!error}
              className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)] flex items-center gap-2 disabled:opacity-40"
              data-testid="pdf-modal-print"
            >
              Stampa
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2"
              data-testid="pdf-modal-download"
            >
              Scarica PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest border border-[var(--c-border-soft)] hover:bg-[var(--c-surface)]"
              data-testid="pdf-modal-close"
              aria-label="Chiudi"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 bg-neutral-200 overflow-auto p-4">
          {error && (
            <div className="text-center font-mono text-sm text-red-600 py-12" data-testid="pdf-preview-error">
              Impossibile renderizzare il PDF: {error}
            </div>
          )}
          {loading && !error && (
            <div className="text-center font-mono text-xs text-[var(--c-text-muted)] py-12">
              Generazione anteprima…
            </div>
          )}
          <div ref={containerRef} data-testid="pdf-pages-container" />
        </div>
      </div>
    </div>
  );
}
